import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { commercialCyclesApi, CommercialCycle, GenerateDraftRun } from "@/lib/api";
import {
  CycleAlreadyGeneratedError,
  isCycleAlreadyGeneratedError,
  RolloverAlreadyScheduledError,
  isRolloverAlreadyScheduledError,
  isRolloverScheduleConflictError,
} from "@/lib/cycleError";
import { formatDateTimeValue } from "./constants";

/** Valor mínimo aceito pelo `<input type="datetime-local">`, no fuso local do navegador. */
function nowLocalDateTimeValue(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

interface ScheduleRolloverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cycles: CommercialCycle[];
  pendingSchedules: GenerateDraftRun[];
  onScheduled: () => void;
}

export function ScheduleRolloverDialog({
  open,
  onOpenChange,
  cycles,
  pendingSchedules,
  onScheduled,
}: ScheduleRolloverDialogProps) {
  const [sourceYear, setSourceYear] = useState<string>("");
  const [targetYear, setTargetYear] = useState<string>("");
  const [scheduledAtLocal, setScheduledAtLocal] = useState<string>("");
  const [alreadyGeneratedError, setAlreadyGeneratedError] = useState<CycleAlreadyGeneratedError | null>(null);
  const [alreadyScheduledError, setAlreadyScheduledError] = useState<RolloverAlreadyScheduledError | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const resetForm = () => {
    setSourceYear("");
    setTargetYear("");
    setScheduledAtLocal("");
    setAlreadyGeneratedError(null);
    setAlreadyScheduledError(null);
    setGenericError(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const existingScheduleForPair = sourceYear && targetYear
    ? pendingSchedules.find(
        (s) => s.sourceYear === Number(sourceYear) && s.targetYear === Number(targetYear),
      )
    : null;

  const scheduledDate = scheduledAtLocal ? new Date(scheduledAtLocal) : null;
  const isDateInFuture = !!scheduledDate && !Number.isNaN(scheduledDate.getTime()) && scheduledDate.getTime() > Date.now();

  const scheduleMutation = useMutation({
    mutationFn: () =>
      commercialCyclesApi.scheduleRollover(
        Number(sourceYear),
        Number(targetYear),
        (scheduledDate as Date).toISOString(),
      ),
    onSuccess: () => {
      toast.success(`Tombamento ${sourceYear} → ${targetYear} agendado com sucesso.`);
      onScheduled();
      handleClose();
    },
    onError: (error) => {
      const alreadyGenerated = isCycleAlreadyGeneratedError(error);
      if (alreadyGenerated) {
        setAlreadyGeneratedError(alreadyGenerated);
        return;
      }
      const alreadyScheduled = isRolloverAlreadyScheduledError(error);
      if (alreadyScheduled) {
        setAlreadyScheduledError(alreadyScheduled);
        return;
      }
      const conflict = isRolloverScheduleConflictError(error);
      if (conflict) {
        setGenericError(conflict.message);
        return;
      }
      toast.error("Erro ao agendar o tombamento.");
    },
  });

  const canSubmit = !!sourceYear && !!targetYear && isDateInFuture && !existingScheduleForPair;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : handleClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Agendar tombamento</DialogTitle>
          <DialogDescription>
            Agenda o tombamento aditivo (mesma operação do "Gerar rascunho") para disparar
            automaticamente numa data futura. O disparo real ocorre a partir da data agendada,
            podendo levar até ~1h (checagem periódica em segundo plano).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ciclo de origem</Label>
            <Select
              value={sourceYear}
              onValueChange={(v) => {
                setSourceYear(v);
                setTargetYear(String(Number(v) + 1));
                setAlreadyGeneratedError(null);
                setAlreadyScheduledError(null);
                setGenericError(null);
              }}
              disabled={scheduleMutation.isPending}
            >
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {cycles.map((c) => (
                  <SelectItem key={c.year} value={String(c.year)}>{c.year} ({c.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Ciclo de destino</Label>
            <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
              {targetYear || "—"}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          O ciclo de destino não precisa existir ainda — ele é criado quando o tombamento dispara.
        </p>

        <div className="space-y-2">
          <Label>Data e hora do disparo</Label>
          <Input
            type="datetime-local"
            value={scheduledAtLocal}
            min={nowLocalDateTimeValue()}
            onChange={(e) => setScheduledAtLocal(e.target.value)}
            disabled={scheduleMutation.isPending}
          />
          <p className="text-xs text-muted-foreground">
            Horário local do seu navegador. O disparo pode ocorrer a partir desse momento, com até
            ~1h de atraso.
          </p>
        </div>

        {existingScheduleForPair && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Já existe um agendamento pendente para {existingScheduleForPair.sourceYear} →{" "}
              {existingScheduleForPair.targetYear}, para{" "}
              {formatDateTimeValue(existingScheduleForPair.scheduledAt)} (criado por{" "}
              {existingScheduleForPair.triggeredBy || "—"}). Cancele-o na lista de agendamentos
              pendentes antes de criar um novo.
            </div>
          </div>
        )}

        {alreadyGeneratedError && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{alreadyGeneratedError.message}</div>
          </div>
        )}

        {alreadyScheduledError && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{alreadyScheduledError.message}</div>
          </div>
        )}

        {genericError && (
          <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{genericError}</div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={scheduleMutation.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={!canSubmit || scheduleMutation.isPending}
            onClick={() => scheduleMutation.mutate()}
          >
            {scheduleMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CalendarClock className="h-4 w-4 mr-2" />
            )}
            Agendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
